import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, SimpleGrid, Spinner, Text } from '@chakra-ui/react'

import { useInfiniteCollections } from '@/api/collections'
import AppNavbar from '@/components/app-navbar'
import CollectionCard from '@/components/collection-card'
import IconFolder from '@/components/icons/IconFolder'
import IconPlus from '@/components/icons/IconPlus'
import Button from '@/components/ui/Button'
import { H3 } from '@/components/ui/Headings'
import useDebouncedValue from '@/hooks/useDebouncedValue'
import DeleteGroupModal from '@/views/library/components/delete-group-modal'
import IPGroupModal from '@/views/library/components/ip-group-modal'
import LibraryHeader from '@/views/library/components/library-header'

import type { Collection } from '@/api/collections'

export default function LibraryPage() {
  const navigate = useNavigate()

  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebouncedValue(searchTerm)

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteCollections({
      searchTerm: debouncedSearchTerm || undefined
    })

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCollection, setEditingCollection] = useState<Collection | null>(
    null
  )
  const [deletingCollection, setDeletingCollection] =
    useState<Collection | null>(null)

  const items = data?.pages.flatMap((page) => page.items) ?? []
  const total = data?.pages[0]?.count ?? 0

  const observer = useRef<IntersectionObserver | null>(null)

  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetchingNextPage) return

      if (observer.current) {
        observer.current.disconnect()
      }

      if (!hasNextPage) return

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage()
        }
      })

      if (node) {
        observer.current.observe(node)
      }
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  )

  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [])

  const handleCollectionClick = (id: string) => {
    navigate(`/library/collection/${id}`)
  }

  const handleCollectionDelete = (id: string) => {
    const collection = items.find((item) => item.id === id)
    if (!collection) return
    setDeletingCollection(collection)
  }

  const handleDeleteModalClose = () => {
    setDeletingCollection(null)
  }

  const handleCollectionCreate = () => {
    setEditingCollection(null)
    setIsModalOpen(true)
  }

  const handleCollectionEdit = (id: string) => {
    const collection = items.find((item) => item.id === id)
    if (!collection) return
    setEditingCollection(collection)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingCollection(null)
  }

  return (
    <Box minH="100vh" bg="bg.primary">
      <AppNavbar />
      <Box px={{ base: '16px', md: '32px' }} py={6}>
        <LibraryHeader
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          onCreateClick={handleCollectionCreate}
        />
        {isLoading ? (
          <Flex justify="center" align="center" minH="400px">
            <Spinner size="xl" color="accent.500" />
          </Flex>
        ) : (
          <Flex direction="column" h="100%" gap={3}>
            <Text textStyle="bodySmall" color="text.secondary">
              IP Groups ({total})
            </Text>

            {!items.length ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                minH="400px"
                gap="24px"
              >
                <Flex direction="column" align="center" gap="12px">
                  <IconFolder boxSize="48px" color="text.tertiary" />
                  <H3 color="text.primary" fontWeight={500}>
                    {debouncedSearchTerm
                      ? 'No IP groups match your search'
                      : 'Create your first IP Group'}
                  </H3>
                  {!debouncedSearchTerm && (
                    <Text textStyle="body" color="text.secondary">
                      Organize your graphics into collections
                    </Text>
                  )}
                </Flex>
                {!debouncedSearchTerm && (
                  <Button
                    variant="primary"
                    onClick={handleCollectionCreate}
                  >
                    <Flex align="center" gap="6px">
                      <IconPlus boxSize="20px" />
                      Create IP Group
                    </Flex>
                  </Button>
                )}
              </Flex>
            ) : (
              <>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
                  {items.map((collection, index) => (
                    <Box
                      key={collection.id}
                      ref={
                        index === items.length - 1 ? lastElementRef : undefined
                      }
                    >
                      <CollectionCard
                        collection={collection}
                        onClick={handleCollectionClick}
                        onEdit={handleCollectionEdit}
                        onDelete={handleCollectionDelete}
                      />
                    </Box>
                  ))}
                </SimpleGrid>
                {isFetchingNextPage && (
                  <Box display="flex" justifyContent="center" py={6}>
                    <Spinner size="md" color="accent.500" />
                  </Box>
                )}
              </>
            )}
          </Flex>
        )}

        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          p={4}
          bg="bg.secondary"
          borderTopWidth="1px"
          borderColor="border.subtle"
          display={{ base: 'block', md: 'none' }}
          zIndex={10}
        >
          <Button variant="primary" w="full" onClick={handleCollectionCreate}>
            <Flex align="center" justify="center" gap={2}>
              <IconPlus boxSize={5} />
              Create IP Group
            </Flex>
          </Button>
        </Box>

        <IPGroupModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          collection={editingCollection}
        />

        <DeleteGroupModal
          isOpen={!!deletingCollection}
          onClose={handleDeleteModalClose}
          collection={deletingCollection}
        />
      </Box>
    </Box>
  )
}
